import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import Modal from './ui/Modal.jsx';
import { RELEASES, CURRENT_VERSION } from '../lib/releaseNotes.js';
import { cn } from '@/lib/utils';

const SEEN_KEY = 'prohr_whatsnew_seen';

function getSeenVersion() {
  try {
    return localStorage.getItem(SEEN_KEY);
  } catch {
    return CURRENT_VERSION;
  }
}

// Ephemeral "What's new" affordance: a pink banner block that sits directly
// above the user/profile box (same width), shown ONLY when there are unseen
// releases. Clicking opens a full centered popup listing ALL updates, with
// the ones the user hasn't seen yet flagged "New". Multiple missed releases
// consolidate into this single stacked list (with a count badge) — never a
// pile of separate notifications. One click marks them all seen, and once
// viewed + closed the banner disappears until the next release bumps
// CURRENT_VERSION. Not a permanent button.
export default function WhatsNew({ collapsed = false }) {
  const [open, setOpen] = useState(false);
  const [seenVersion, setSeenVersion] = useState(getSeenVersion);
  // Captured at open time (before we mark everything seen) so the "New"
  // flags persist while the popup is open.
  const [newAtOpen, setNewAtOpen] = useState(0);

  // RELEASES is newest-first. Everything before the last-seen release is
  // unseen; if the user has never looked (or the stored version is unknown),
  // all releases count as unseen.
  const seenIdx = RELEASES.findIndex((r) => r.version === seenVersion);
  const unseenCount = seenIdx === -1 ? RELEASES.length : seenIdx;
  const hasUnseen = unseenCount > 0;

  const handleOpen = () => {
    setNewAtOpen(unseenCount);
    setOpen(true);
    try { localStorage.setItem(SEEN_KEY, CURRENT_VERSION); } catch {}
    setSeenVersion(CURRENT_VERSION);
  };

  // Only present while there's something unseen (or while the popup is still
  // open after a click). Nothing renders otherwise — no leftover line.
  if (!hasUnseen && !open) return null;

  return (
    <>
      <div className={cn('shrink-0 px-3 pb-2.5', collapsed && 'flex justify-center')}>
        {collapsed ? (
          <button
            onClick={handleOpen}
            title={`What's new${unseenCount > 0 ? ` (${unseenCount})` : ''}`}
            className="relative flex h-9 w-9 items-center justify-center rounded-lg border border-profun-pink/30 bg-profun-pink/10 text-profun-pink transition-colors hover:bg-profun-pink/20"
          >
            <Sparkles className="h-4 w-4" />
            {unseenCount > 0 && (
              <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-profun-pink px-1 text-[10px] font-bold leading-none text-white ring-2 ring-card">
                {unseenCount}
              </span>
            )}
          </button>
        ) : (
          <button
            onClick={handleOpen}
            className="flex w-full items-center gap-2.5 rounded-lg border border-profun-pink/30 bg-profun-pink/10 px-3 py-2.5 text-left text-profun-pink transition-colors hover:bg-profun-pink/20"
          >
            <Sparkles className="h-4 w-4 shrink-0" />
            <div className="min-w-0 flex-1 leading-tight">
              <div className="text-sm font-semibold">What's new</div>
              <div className="text-[11px] text-profun-pink/70">
                {unseenCount > 0
                  ? `${unseenCount} new update${unseenCount !== 1 ? 's' : ''}`
                  : 'See the latest updates'}
              </div>
            </div>
            {unseenCount > 0 && (
              <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-profun-pink px-1.5 text-[11px] font-bold leading-none text-white">
                {unseenCount}
              </span>
            )}
          </button>
        )}
      </div>

      <Modal
        isOpen={open}
        onClose={() => setOpen(false)}
        title="What's new in KSA ProHR"
        width="min(700px, 95vw)"
        className="border-profun-pink/30"
      >
        {newAtOpen > 0 && (
          <div className="mb-4 rounded-lg bg-profun-pink/10 px-3 py-2 text-sm font-medium text-profun-pink">
            {newAtOpen} new update{newAtOpen !== 1 ? 's' : ''} since you last looked
          </div>
        )}
        <div className="space-y-6">
          {RELEASES.map((release, ri) => {
            const isNew = ri < newAtOpen;
            return (
              <div key={release.version}>
                <div className="mb-3 flex items-center justify-between gap-3 border-b border-profun-pink/20 pb-2">
                  <h3 className="flex items-center gap-2 text-base font-bold text-foreground">
                    {release.title}
                    {isNew && (
                      <span className="rounded-full bg-profun-pink/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-profun-pink">
                        New
                      </span>
                    )}
                  </h3>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {new Date(release.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
                <div className="space-y-3">
                  {release.items.map((item, i) => (
                    <div key={i} className="rounded-lg border border-border bg-muted/20 p-4">
                      <div className="text-sm font-semibold text-foreground">{item.title}</div>
                      <p className="mt-1 text-sm text-muted-foreground">{item.detail}</p>
                      {item.howTo && (
                        <p className="mt-2 text-xs text-profun-pink">
                          <span className="font-semibold">How to use: </span>
                          {item.howTo}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </Modal>
    </>
  );
}
